import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { renameNoneForViewBy } from '../constants';

import {
  buildEntityIdMap,
  buildEntityNameColumn,
  CELL_PADDING_FIXED,
  CELL_PADDING_LEFT,
  GreenText,
  RedText,
  STRETCH_CELL,
} from './shared';

export type ReferralsRow = {
  entityName: string;
  entityId: string;
  totalReferrals: number;
  acceptedRate: number | null;
  denialRate: number | null;
  createdToDischargeDays: number | null;
  acceptedToDischargeDays: number | null;
};

export function buildReferralsRows(
  rollups: AnalyticsResponseRollupCollection,
  viewBy?: string,
  groupingIndex = 1
): ReferralsRow[] {
  const entityGrouping = rollups.groupings[groupingIndex];
  if (!entityGrouping) return [];

  const idByName = buildEntityIdMap(rollups);
  const entityNames = entityGrouping.dimensions.getCurrentDimensionValuesByIndex(0);
  const totalReferrals = entityGrouping.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT);
  const acceptedRates = entityGrouping.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
  const denialRates = entityGrouping.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
  const createdToDischargeDays = entityGrouping.metrics.getCurrentSeries(Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE);
  const acceptedToDischargeDays = entityGrouping.metrics.getCurrentSeries(Metrics.ACCEPTED_TO_DISCHARGE_DAYS_AVERAGE);

  return entityNames
    .map((name, i) => ({
      entityName: renameNoneForViewBy(name, viewBy),
      entityId: idByName.get(name) ?? '',
      totalReferrals: totalReferrals[i] ?? 0,
      acceptedRate: acceptedRates[i] ?? null,
      denialRate: denialRates[i] ?? null,
      createdToDischargeDays: createdToDischargeDays[i] ?? null,
      acceptedToDischargeDays: acceptedToDischargeDays[i] ?? null,
    }))
    .filter((row) => row.entityName);
}

export const REFERRALS_DEFAULT_SORT = { id: 'totalReferrals', desc: true };

export function buildReferralsColumns(
  entityHeader: string,
  onEntityClick: (entityId: string, entityName: string) => void
): ColumnDef<ReferralsRow, any>[] {
  const helper = createColumnHelper<ReferralsRow>();

  return [
    buildEntityNameColumn<ReferralsRow>(entityHeader, onEntityClick),
    helper.accessor('totalReferrals', {
      header: 'Total Referrals',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 140,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('acceptedRate', {
      header: 'Acceptance Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 165,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('denialRate', {
      header: 'Denial Rate (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 135,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('createdToDischargeDays', {
      header: 'Avg. Created to Discharge',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 185,
      enableSorting: true,
      ...CELL_PADDING_FIXED,
    }),
    helper.accessor('acceptedToDischargeDays', {
      header: 'Avg. Accepted to Discharge',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 205,
      enableSorting: true,
      ...CELL_PADDING_FIXED,
    }),
  ];
}
